import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  FaMoneyBillWave, FaReceipt, FaEye, FaExclamationTriangle, FaBoxOpen, FaArrowRight, FaFire,
} from 'react-icons/fa'
import StatTile from '../components/StatTile'
import RevenueChart from '../components/charts/RevenueChart'
import BarList from '../components/charts/BarList'
import PipelineBar from '../components/charts/PipelineBar'
import Thumb from '../components/Thumb'
import { Card, Spinner, Banner, PageHeader, StatusBadge, EmptyState } from '../components/ui'
import { SampleDataNotice } from '../components/Notices'
import { money, moneyShort, number, formatDateShort, relativeTime, changePct, signedPercent } from '../lib/format'
import { STATUS } from '../lib/viz'
import { SHOW_STOCK } from '../lib/features'
import * as api from '../lib/api'

/**
 * The landing screen. It has one job: answer "is the shop all right today?"
 * without the operator having to go looking.
 *
 * Ordered by urgency, not by how interesting the data is:
 *   1. the four numbers that describe trading right now;
 *   2. anything that needs a human (unpaid orders, empty shelves);
 *   3. the revenue trend;
 *   4. what is selling and what is merely being looked at.
 */

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
]

const DashboardPage = () => {
  const [range, setRange] = useState(30)
  const [state, setState] = useState({ loading: true, error: null })
  const [summary, setSummary] = useState(null)
  const [sales, setSales] = useState([])
  const [previousSales, setPreviousSales] = useState([])
  const [performance, setPerformance] = useState([])
  const [trending, setTrending] = useState([])
  const [categories, setCategories] = useState([])
  const [lowStock, setLowStock] = useState([])
  const [statusCounts, setStatusCounts] = useState(new Map())
  const [recentOrders, setRecentOrders] = useState([])
  const [overdueInvoices, setOverdueInvoices] = useState([])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setState({ loading: true, error: null })
      try {
        // Fetched together: the panels are read as one picture, so a staggered
        // arrival would show a dashboard that briefly contradicts itself.
        const [sum, daily, perf, trend, cats, low, counts, orders, invoices] = await Promise.all([
          api.getDashboardSummary(),
          api.getDailySales({ days: range * 2 }), // double, so the previous period is in hand
          api.getProductPerformance(),
          api.getTrending(),
          api.getCategoryPerformance(),
          // Not asked for at all while stock is hidden — an empty panel is one
          // thing, a query for figures nobody can act on is another.
          SHOW_STOCK ? api.getLowStock() : Promise.resolve([]),
          api.getOrderStatusCounts(),
          api.getOrders({ limit: 6 }),
          // Allowed to fail on its own: unpaid invoices are one line on this
          // page, and a dashboard that will not open because of them would be a
          // poor trade.
          api.getInvoices({ status: 'sent' }).catch(() => []),
        ])
        if (cancelled) return

        setSummary(sum)
        setSales(daily.slice(-range))
        setPreviousSales(daily.slice(0, range))
        setPerformance(perf ?? [])
        setTrending(trend ?? [])
        setCategories(cats ?? [])
        setLowStock(low ?? [])
        setStatusCounts(counts ?? new Map())
        setRecentOrders(orders ?? [])
        setOverdueInvoices((invoices ?? []).filter((invoice) => invoice.is_overdue))
        setState({ loading: false, error: null })
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: err })
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [range])

  const totals = useMemo(() => {
    const sum = (rows, key) => rows.reduce((n, r) => n + (Number(r[key]) || 0), 0)
    const revenue = sum(sales, 'revenue')
    const previousRevenue = sum(previousSales, 'revenue')
    const orders = sum(sales, 'order_count')
    const previousOrders = sum(previousSales, 'order_count')

    return {
      revenue,
      revenueChange: changePct(revenue, previousRevenue),
      orders,
      ordersChange: changePct(orders, previousOrders),
      // Average order value, not revenue ÷ days: the former is a number the
      // owner can act on by bundling, the latter just restates revenue.
      aov: orders > 0 ? revenue / orders : 0,
      previousAov: previousOrders > 0 ? previousRevenue / previousOrders : 0,
      sparkline: sales.map((r) => Number(r.revenue) || 0),
    }
  }, [sales, previousSales])

  /**
   * Products with traffic but no sales.
   *
   * The single most actionable list on the page, and one that sales data alone
   * cannot produce. A garment with 200 views and nothing sold has a bad photo or
   * the wrong price, and that is fixable today.
   */
  const browseNoBuy = useMemo(
    () =>
      (performance ?? [])
        .filter((p) => (p.views ?? 0) >= 25 && (p.units_sold ?? 0) === 0)
        .sort((a, b) => (b.views ?? 0) - (a.views ?? 0))
        .slice(0, 5),
    [performance],
  )

  const risers = useMemo(
    () =>
      (trending ?? [])
        .filter((t) => t.units_7d > 0 && (t.units_change_pct ?? 0) > 0)
        .sort((a, b) => (b.units_change_pct ?? 0) - (a.units_change_pct ?? 0))
        .slice(0, 5),
    [trending],
  )

  if (state.loading) return <Spinner label="Loading the dashboard…" />

  if (state.error) {
    return (
      <Banner tone="error">
        <strong>Could not load the dashboard.</strong> {state.error.message}
      </Banner>
    )
  }

  const needsAttention = [
    summary?.pending_orders > 0 && {
      tone: 'warning',
      text: `${summary.pending_orders} order${summary.pending_orders === 1 ? '' : 's'} awaiting payment`,
      to: '/orders?status=pending',
    },
    summary?.orders_to_fulfil > 0 && {
      tone: 'info',
      text: `${summary.orders_to_fulfil} order${summary.orders_to_fulfil === 1 ? '' : 's'} to pack and ship`,
      to: '/orders?status=paid',
    },
    SHOW_STOCK && summary?.low_stock_lines > 0 && {
      tone: 'warning',
      text: `${summary.low_stock_lines} size${summary.low_stock_lines === 1 ? '' : 's'} low or out of stock`,
      to: '/inventory?filter=low',
    },
    SHOW_STOCK && summary?.out_of_stock_products > 0 && {
      tone: 'critical',
      text: `${summary.out_of_stock_products} product${summary.out_of_stock_products === 1 ? '' : 's'} unavailable on the site`,
      to: '/inventory?filter=out',
    },
    // Money already earned and not yet collected belongs on the same list as
    // stock to reorder. An invoice goes unchased because nobody is looking at
    // the invoice page, not because nobody cares.
    overdueInvoices.length > 0 && {
      tone: 'critical',
      text: `${money(overdueInvoices.reduce((sum, i) => sum + Number(i.balance_due ?? i.total), 0))} overdue across ${overdueInvoices.length} invoice${overdueInvoices.length === 1 ? '' : 's'}`,
      to: '/invoices',
    },
  ].filter(Boolean)

  return (
    <div className="space-y-5">
      <PageHeader title="Dashboard" subtitle={`Trading summary for the last ${range} days`}>
        <div className="flex rounded-lg border border-hairline bg-surface p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setRange(r.days)}
              className={`rounded-md px-2.5 py-1 text-2xs font-medium transition-colors ${
                range === r.days ? 'bg-brand-navy text-white' : 'text-ink-secondary hover:text-ink'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </PageHeader>

      <SampleDataNotice />

      {/* ---- Headline numbers ------------------------------------------- */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label={`Revenue · ${range}d`}
          value={money(totals.revenue)}
          change={totals.revenueChange}
          changeLabel={`vs previous ${range}d`}
          sparkline={totals.sparkline}
          icon={FaMoneyBillWave}
        />
        <StatTile
          label={`Orders · ${range}d`}
          value={number(totals.orders)}
          change={totals.ordersChange}
          changeLabel={`vs previous ${range}d`}
          icon={FaReceipt}
        />
        <StatTile
          label="Average order"
          value={money(totals.aov)}
          change={changePct(totals.aov, totals.previousAov)}
          changeLabel={`vs previous ${range}d`}
          icon={FaMoneyBillWave}
        />
        <StatTile
          label="Product views · 7d"
          value={number(summary?.views_7d ?? 0)}
          hint={`${number(summary?.sessions_7d ?? 0)} browsing sessions`}
          icon={FaEye}
        />
      </div>

      {/* ---- Needs attention -------------------------------------------- */}
      {needsAttention.length > 0 && (
        <Card title="Needs attention" subtitle="Things waiting on someone">
          <ul className="grid gap-2 sm:grid-cols-2">
            {needsAttention.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="flex items-center gap-2.5 rounded-lg border border-hairline px-3 py-2.5 transition-colors hover:border-baseline hover:bg-plane/60"
                >
                  <FaExclamationTriangle
                    className="shrink-0 text-[12px]"
                    style={{
                      color: item.tone === 'critical' ? STATUS.critical : item.tone === 'warning' ? STATUS.warning : '#2a78d6',
                    }}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{item.text}</span>
                  <FaArrowRight className="shrink-0 text-[10px] text-ink-muted" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* ---- Revenue trend + pipeline ------------------------------------ */}
      <div className="grid gap-4 xl:grid-cols-3">
        <Card
          title="Revenue"
          subtitle={`Paid orders, ${formatDateShort(sales[0]?.day)} – ${formatDateShort(sales[sales.length - 1]?.day)}`}
          className="xl:col-span-2"
        >
          <RevenueChart data={sales} comparison={previousSales} />
        </Card>

        <Card title="Order pipeline" subtitle="Every order, by where it sits">
          <PipelineBar counts={statusCounts} />
        </Card>
      </div>

      {/* ---- What is selling -------------------------------------------- */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card
          title="Best sellers by revenue"
          subtitle="All time"
          action={
            <Link to="/analytics" className="btn-subtle btn-sm">
              Full report
            </Link>
          }
        >
          <BarList
            data={performance}
            valueKey="revenue"
            formatValue={moneyShort}
            secondary={(row) => `${number(row.units_sold)} sold · ${row.category ?? 'Uncategorised'}`}
            limit={7}
          />
        </Card>

        <Card title="Revenue by category" subtitle="All time">
          <BarList
            data={categories}
            valueKey="revenue"
            formatValue={moneyShort}
            secondary={(row) => `${number(row.product_count)} products · ${number(row.units_sold)} sold`}
            limit={7}
          />
        </Card>
      </div>

      {/* ---- Momentum and missed opportunities -------------------------- */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Moving this week" subtitle="Units sold, last 7 days versus the 7 before">
          {risers.length === 0 ? (
            <EmptyState icon={FaFire} title="Nothing trending yet">
              Once a product sells more this week than last, it shows up here.
            </EmptyState>
          ) : (
            <ul className="divide-y divide-hairline/70">
              {risers.map((row) => (
                <li key={row.id} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                  <Thumb src={row.image} alt={row.name} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium text-ink">{row.name}</div>
                    <div className="text-2xs text-ink-muted">
                      {row.units_7d} sold this week · was {row.units_prev_7d}
                    </div>
                  </div>
                  <span
                    className="shrink-0 text-[13px] font-semibold tabular-nums"
                    style={{ color: STATUS.goodText }}
                  >
                    {signedPercent(row.units_change_pct)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card
          title="Looked at, not bought"
          subtitle="25+ views and no sales — usually a photo or a price problem"
        >
          {browseNoBuy.length === 0 ? (
            <EmptyState icon={FaEye} title="Nothing flagged">
              Every product with real traffic has sold at least once.
            </EmptyState>
          ) : (
            <ul className="divide-y divide-hairline/70">
              {browseNoBuy.map((row) => (
                <li key={row.id} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                  <Thumb src={row.image} alt={row.name} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium text-ink">{row.name}</div>
                    <div className="text-2xs text-ink-muted">
                      {money(row.price)} · {row.category ?? 'Uncategorised'}
                    </div>
                  </div>
                  <span className="shrink-0 text-right">
                    <span className="block text-[13px] font-semibold tabular-nums text-ink">
                      {number(row.views)}
                    </span>
                    <span className="block text-2xs text-ink-muted">views</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* ---- Recent orders + restock list ------------------------------- */}
      {/* With the restock list hidden, the orders table takes the whole row
          rather than sitting in a half-width column beside nothing. */}
      <div className={`grid gap-4 ${SHOW_STOCK ? 'lg:grid-cols-2' : ''}`}>
        <Card
          title="Latest orders"
          action={
            <Link to="/orders" className="btn-subtle btn-sm">
              All orders
            </Link>
          }
          bodyClassName=""
        >
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order) => (
                  <tr key={order.id}>
                    <td>
                      <Link to={`/orders/${order.id}`} className="font-medium text-ink hover:text-brand-red">
                        {order.order_number}
                      </Link>
                      <div className="text-2xs text-ink-muted">{relativeTime(order.placed_at)}</div>
                    </td>
                    <td className="max-w-[140px] truncate">
                      {order.customer_first_name} {order.customer_last_name}
                    </td>
                    <td>
                      <StatusBadge status={order.status} />
                    </td>
                    <td className="num font-medium">{money(order.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {SHOW_STOCK && (
        <Card
          title="Restock list"
          subtitle="Sizes at or below the low-stock threshold"
          action={
            <Link to="/inventory" className="btn-subtle btn-sm">
              Inventory
            </Link>
          }
          bodyClassName=""
        >
          {lowStock.length === 0 ? (
            <EmptyState icon={FaBoxOpen} title="Nothing low">
              Every tracked size is above its threshold.
            </EmptyState>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Size / colour</th>
                    <th className="num">Left</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStock.slice(0, 8).map((row) => (
                    <tr key={row.inventory_id ?? row.id}>
                      <td className="max-w-[180px]">
                        <div className="truncate font-medium text-ink">{row.name}</div>
                        <div className="text-2xs text-ink-muted">{row.sku}</div>
                      </td>
                      <td className="text-2xs text-ink-secondary">
                        {[row.size, row.color].filter(Boolean).join(' · ') || '—'}
                      </td>
                      <td className="num">
                        <span
                          className="font-semibold tabular-nums"
                          style={{ color: row.available === 0 ? STATUS.critical : STATUS.warning }}
                        >
                          {row.available}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        )}
      </div>
    </div>
  )
}

export default DashboardPage
